/**
 * Custom Functions — Code editor for TypeScript/Python tool functions
 *
 * Lists custom functions with status, language, metrics.
 * Click to view/edit code in Monaco editor panel.
 * Supports: create, edit, test, deploy workflow.
 */

import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Search, Code, Plus, MoreHorizontal, Play, Trash2,
  Rocket, Pause, FileCode2, Sparkles,
  CheckCircle, XCircle, Loader2, Maximize2, Minimize2,
  Copy, AlignLeft, Lock,
} from 'lucide-react';
// Wave C PR-F (2026-05-13): replaced inline <Editor> + 14-option block
// with the shared <FunctionSourceEditor variant="tenant-sheet" /> primitive.
// The variant preserves this surface's exact pre-refactor visual rendering
// (theme follows useTheme(), minimap toggles with fullscreen, fontSize=13,
// padding=12, bracket-pair colorization, indent guides, smooth scrolling,
// smooth cursor blink). The onMount escape hatch still wires cursor-
// position tracking, the Ctrl+S keybinding, and the TS-diagnostics tuning.
//
// See: components/function-editor-shared/ for the variant matrix.
import { FunctionSourceEditor } from '@/components/function-editor-shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useTheme } from '@/components/theme-provider';
import {
  FUNCTION_STATUS_STYLES,
  type CustomFunction,
  type FunctionLanguage,
  type FunctionStatus,
} from '@/lib/mock-connections-data';
import {
  useTenantFunctions,
  useDeployFunction,
  useDisableFunction,
  useDeleteFunction,
  useSaveFunctionSource,
  useTestFunction,
  type FunctionTestPerInput,
} from '@/lib/use-connections-module';
import { useAuth } from '@/contexts/auth-context';
import { useHasPermission } from '@/lib/use-has-permission';
import { decideConnectionsRoleGate } from './-connections-role-gate.helpers';

export const Route = createFileRoute('/connections/functions')({
  component: FunctionsPage,
});

const LANGUAGE_LABELS: Record<FunctionLanguage, { label: string; color: string }> = {
  typescript: { label: 'TypeScript', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  python: { label: 'Python', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
};

// Round 4 #228 — exported with an optional initialFunctionId so the
// nested-IA alias at /connections/functions/$functionId can deep-link
// to a specific function and auto-open the editor sheet. The default
// (no prop) preserves the existing /connections/functions list view.
export function FunctionsPage({
  initialFunctionId,
  indexPath = '/connections/functions',
}: { initialFunctionId?: string; indexPath?: string } = {}) {
  const navigate = useNavigate();
  // Codex retest #228 round 5 — TanStack treats functions.new.tsx +
  // functions.$functionId.tsx as children of THIS route. The list
  // used to render unconditionally with no <Outlet />, so children
  // never painted. Hand off when the path isn't bare functions URL.
  // See automate/playbooks.tsx (#169) for hook-ordering rationale.
  const location = useLocation();
  const { mode } = useTheme();
  const editorRef = useRef<any>(null);
  const [search, setSearch] = useState('');
  // Real-data: tenant_functions adapted to CustomFunction VM. Status comes
  // from the DB `state` column, not a hardcoded fallback.
  const { data: realFns = [] } = useTenantFunctions();
  // Bug #758: hide New Function CTA for tenants below power_user. The
  // /connections/new-function route also gates internally, but the
  // inert CTA advertises capability the role doesn't have.
  // Bug #758 / B1-T3: hide the CTA for tenants without write:connections.
  const { contextKind } = useAuth();
  const canWriteConnections = useHasPermission('write', 'connections');
  const canCreateFunction =
    decideConnectionsRoleGate({ contextKind, canWriteConnections }) !==
    'deny_role_too_low';
  const STATE_TO_STATUS: Record<string, FunctionStatus> = {
    draft: 'draft',
    testing: 'testing',
    review: 'review',
    active: 'active',
    disabled: 'disabled',
    error: 'error',
  };
  // Codex retest #242 — schema check constraint stores runtime as
  // 'ts' | 'py' (see tenant-functions.ts comment), but a user-friendly
  // 'typescript' | 'python' may also appear depending on which path
  // wrote the row. The previous mapping only matched the literal
  // 'python', so a Python function written with runtime='py' fell into
  // the typescript fallback and the badge / editor language picker
  // both lied about the source language. Treat 'py' (canonical) and
  // 'python' (legacy) as Python; everything else stays TypeScript.
  const PYTHON_RUNTIMES = new Set(['py', 'python']);
  // #769 — memoize the adapter against realFns so editorFn / filtered
  // / deleteTarget consumers see stable object references across
  // renders. Pre-fix `functions` was rebuilt on every render, which
  // matches the React-#185 anti-pattern (12daa2e6 trust/audit fix) —
  // any consumer that hangs an effect on `functions` would loop.
  const functions = useMemo<CustomFunction[]>(() => realFns.map((f) => ({
    id: f.id,
    name: f.name,
    displayName: f.displayName,
    description: f.description,
    language: (PYTHON_RUNTIMES.has(f.runtime)
      ? 'python'
      : 'typescript') as FunctionLanguage,
    status: STATE_TO_STATUS[f.state] ?? 'draft',
    code: f.source,
    // Codex retest #244 — function telemetry is not yet collected, so
    // hardcoding zero into stat slots produced a false "Tools 0 /
    // Calls 0 / Latency 0ms" reading on every card. Pass through null
    // to the cards and let them render `—` honestly until the rollup
    // ships. `metrics` matches what real telemetry will look like
    // once the per-function aggregator lands.
    toolCount: null,
    version: f.runtimeVersion,
    author: '—',
    docker: {
      image: 'isolated-vm',
      cpuLimit: String((f.resourceLimits.cpu_ms as number | undefined) ?? 5000),
      memoryLimit: `${(f.resourceLimits.memory_mb as number | undefined) ?? 256}Mi`,
      sandboxed: true,
    },
    metrics: { callCount: null, avgLatencyMs: null, errorRate: null },
    lastDeployed: f.activatedAt ?? null,
    createdAt: f.createdAt,
    updatedAt: f.updatedAt,
    isSystem: f.isSystem ?? false,
  })), [realFns]);
  const [statusFilter, setStatusFilter] = useState<'all' | FunctionStatus>('all');
  const [langFilter, setLangFilter] = useState<'all' | FunctionLanguage>('all');
  const [selectedFnId, setSelectedFnId] = useState<string | null>(null);
  const editorFn = selectedFnId ? functions.find(f => f.id === selectedFnId) ?? null : null;
  const [editorCode, setEditorCode] = useState('');
  const [editorDirty, setEditorDirty] = useState(false);
  const [editorFullscreen, setEditorFullscreen] = useState(false);
  const [cursorPos, setCursorPos] = useState({ line: 1, col: 1 });
  const [deleteTarget, setDeleteTarget] = useState<CustomFunction | null>(null);
  // Sandbox test panel: `testInput` is the author-supplied sample input(s)
  // (a single JSON value, or a JSON array of up to 5 inputs). `testResult`
  // holds the real per-input sandbox envelope (or a client-side parse error).
  const [testInput, setTestInput] = useState('');
  const [testOpen, setTestOpen] = useState(false);
  const [testResult, setTestResult] = useState<
    | { running: boolean; results?: FunctionTestPerInput[]; allPassed?: boolean; error?: string }
    | null
  >(null);

  // #228 — when arriving via /connections/functions/$functionId, seed
  // the editor sheet from the URL once the function list resolves.
  // Guards: only seed once (selectedFnId === null), only seed for ids
  // that actually exist in the loaded data (so a 404-by-typo doesn't
  // silently open nothing or throw).
  useEffect(() => {
    if (!initialFunctionId) return;
    if (selectedFnId !== null) return;
    const fn = functions.find((f) => f.id === initialFunctionId);
    if (!fn) return;
    setSelectedFnId(fn.id);
    setEditorCode(fn.code);
    setEditorDirty(false);
    setTestResult(null);
    setTestOpen(false);
    setTestInput('');
    // functions is recomputed each render; depend on its length and
    // the seed id so this fires once after data loads. Editor code
    // updates are intentionally only applied on the first match.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFunctionId, functions.length]);

  // Filter
  const filtered = functions.filter(fn => {
    if (statusFilter !== 'all' && fn.status !== statusFilter) return false;
    if (langFilter !== 'all' && fn.language !== langFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return fn.name.toLowerCase().includes(q) || fn.displayName.toLowerCase().includes(q) || fn.description.toLowerCase().includes(q);
  });

  // Real backend lifecycle.
  const deployFn = useDeployFunction();
  const disableFn = useDisableFunction();
  const deleteFn = useDeleteFunction();
  const saveSource = useSaveFunctionSource();

  // Persist edited source via the real PATCH /functions/:id endpoint.
  // Editing returns the function to DRAFT (signature invalidated server-side);
  // the user re-deploys to republish. Active functions are rejected (409) —
  // we gate the Save affordance on status !== 'active' so that path is unreachable.
  const handleSaveSource = (id: string, source: string) => {
    const fn = functions.find(f => f.id === id);
    if (!fn) return;
    saveSource.mutate({ id, source }, {
      onSuccess: () => {
        toast.success(`Saved ${fn.displayName} — returned to draft, re-deploy to publish.`);
        setEditorDirty(false);
      },
      onError: (err) => toast.error(`Save failed: ${err.message}`),
    });
  };

  // Ref bridge: the Monaco Ctrl+S keybinding is wired once in onMount and
  // captures stale state. Point a ref at the latest save closure so Ctrl+S
  // always saves the current buffer for the currently-open function.
  const saveSourceRef = useRef<(() => void) | null>(null);
  saveSourceRef.current = () => {
    if (!editorFn) return;
    if (editorFn.status === 'active' || editorFn.isSystem) return;
    if (!editorDirty) return;
    handleSaveSource(editorFn.id, editorCode);
  };

  const handleDeploy = (id: string) => {
    const fn = functions.find(f => f.id === id);
    if (!fn) return;
    deployFn.mutate(id, {
      onSuccess: () => toast.success(`Deployed ${fn.displayName}`),
      onError: (err) => toast.error(`Deploy failed: ${err.message}`),
    });
  };

  const handleDisable = (id: string) => {
    const fn = functions.find(f => f.id === id);
    if (!fn) return;
    disableFn.mutate({ id }, {
      onSuccess: () => toast.success(`Disabled ${fn.displayName}`),
      onError: (err) => toast.error(`Disable failed: ${err.message}`),
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    deleteFn.mutate(target.id, {
      onSuccess: () => {
        toast.success(`Deleted ${target.displayName}`);
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(`Delete failed: ${err.message}`),
    });
  };

  // Real sandbox test — runs the persisted source in the isolated-vm runner
  // (networkHosts=[], 3s/input cap) against the author-supplied sample input.
  const testFn = useTestFunction();

  // Parse the test-input textarea into a sampleInputs array.
  //   - empty                → [{}]  (run once with an empty object)
  //   - a JSON array         → that array (capped at 5, validated server-side)
  //   - any other JSON value → [value] (single input)
  // Returns a typed error string on parse failure so we never send garbage.
  const parseSampleInputs = (raw: string): { ok: true; inputs: unknown[] } | { ok: false; error: string } => {
    const trimmed = raw.trim();
    if (!trimmed) return { ok: true, inputs: [{}] };
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      return { ok: false, error: `Invalid JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
    const inputs = Array.isArray(parsed) ? parsed : [parsed];
    if (inputs.length === 0) return { ok: true, inputs: [{}] };
    if (inputs.length > 5) return { ok: false, error: 'Provide at most 5 sample inputs (a JSON array of ≤5 items).' };
    return { ok: true, inputs };
  };

  const handleRunTest = () => {
    if (!editorFn) return;
    // Open the input panel on first click so the author can supply sample
    // input; subsequent clicks (panel already open) actually run.
    if (!testOpen) {
      setTestOpen(true);
      setTestResult(null);
      return;
    }
    const parsed = parseSampleInputs(testInput);
    if (!parsed.ok) {
      setTestResult({ running: false, error: parsed.error });
      return;
    }
    setTestResult({ running: true });
    testFn.mutate(
      { id: editorFn.id, sampleInputs: parsed.inputs },
      {
        onSuccess: (res) =>
          setTestResult({ running: false, results: res.results, allPassed: res.allPassed }),
        onError: (err) => setTestResult({ running: false, error: err.message }),
      },
    );
  };

  // Now that every hook above has run, hand off to the child route
  // (functions.new / functions.$functionId) when the path isn't the
  // bare list URL. The deep-link via initialFunctionId still works
  // because functions.$functionId.tsx mounts FunctionsPage with the
  // seed param — that flow is independent of this Outlet handoff.
  //
  // 2026-05-12 browser audit P0 — when this same component is mounted at
  // /studio/functions (per the Studio tab fix), the old hard-coded
  // '/connections/functions' compare always failed, so we rendered
  // <Outlet/> with no child and the Studio Functions tab went blank.
  // The route mount points now pass their own `indexPath` so the index
  // check works for any mount. Defaults to /connections/functions to
  // preserve every existing call site.
  const normalizedIndex = indexPath.replace(/\/$/, '');
  const normalizedPath = location.pathname.replace(/\/$/, '');
  const isIndex = normalizedPath === normalizedIndex;
  if (!isIndex && !initialFunctionId) return <Outlet />;

  return (
    <div className="p-6 space-y-6">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search functions..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={langFilter} onValueChange={v => setLangFilter(v as 'all' | FunctionLanguage)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Languages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Languages</SelectItem>
            <SelectItem value="typescript">TypeScript</SelectItem>
            <SelectItem value="python">Python</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as 'all' | FunctionStatus)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="testing">Testing</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
        {canCreateFunction && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => navigate({ to: '/connections/new-function', search: { edit: '' } })}>
            <Plus className="h-3.5 w-3.5" /> New Function
          </Button>
        )}
      </div>

      {/* Function cards */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(fn => (
            <Card key={fn.id} className="hover:border-primary/30 transition-colors group cursor-pointer" onClick={() => { setSelectedFnId(fn.id); setEditorCode(fn.code); setEditorDirty(false); setTestResult(null); setTestOpen(false); setTestInput(''); }}>
              <CardContent className="pt-4 pb-3 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <FileCode2 className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-sm font-medium truncate">{fn.displayName}</p>
                        {fn.isSystem && (
                          <Badge variant="outline" className="shrink-0 text-[9px] px-1 py-0 h-4 gap-0.5 bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300 border-slate-300 dark:border-slate-700" title="Platform-managed system function — read-only">
                            <Lock className="h-2.5 w-2.5" /> System
                          </Badge>
                        )}
                      </div>
                      <p className="text-[10px] font-mono text-muted-foreground truncate">{fn.name}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={e => e.stopPropagation()} aria-label={`Function actions for '${fn.displayName}'`} title="More actions">
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={e => { e.stopPropagation(); setSelectedFnId(fn.id); setEditorCode(fn.code); setEditorDirty(false); setTestResult(null); setTestOpen(false); setTestInput(''); }}>
                        <Code className="h-3.5 w-3.5 mr-2" /> {fn.isSystem ? 'View details (read-only)' : 'View Code'}
                      </DropdownMenuItem>
                      {/* System functions are platform-managed: deploy/disable/delete are
                          sysadmin-only (and rejected server-side for org-scoped callers). */}
                      {!fn.isSystem && fn.status !== 'active' && (
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); handleDeploy(fn.id); }}>
                          <Rocket className="h-3.5 w-3.5 mr-2" /> Deploy
                        </DropdownMenuItem>
                      )}
                      {!fn.isSystem && fn.status === 'active' && (
                        <DropdownMenuItem onClick={e => { e.stopPropagation(); handleDisable(fn.id); }}>
                          <Pause className="h-3.5 w-3.5 mr-2" /> Disable
                        </DropdownMenuItem>
                      )}
                      {!fn.isSystem && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={e => { e.stopPropagation(); setDeleteTarget(fn); }}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Description */}
                <p className="text-[11px] text-muted-foreground line-clamp-2" title={fn.description}>{fn.description}</p>

                {/* Badges */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className={cn('text-[9px]', LANGUAGE_LABELS[fn.language].color)}>
                    {LANGUAGE_LABELS[fn.language].label}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[9px]', FUNCTION_STATUS_STYLES[fn.status])}>
                    {fn.status}
                  </Badge>
                  <Badge variant="secondary" className="text-[9px]">v{fn.version}</Badge>
                </div>

                {/* Stats — Codex retest #244: render `—` when telemetry
                    isn't available yet rather than displaying a hardcoded
                    `0`. The previous "Tools 0 / Calls 0 / Latency —" line
                    was misleading because two of three slots were always
                    fabricated zero. Honest empty state is preferred until
                    per-function rollups ship. */}
                <div className="grid grid-cols-3 gap-2 text-center pt-1">
                  <div>
                    <p className="text-sm font-semibold">{fn.toolCount != null ? fn.toolCount : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Tools</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{fn.metrics.callCount != null ? fn.metrics.callCount.toLocaleString() : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Calls</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{fn.metrics.avgLatencyMs != null && fn.metrics.avgLatencyMs > 0 ? `${fn.metrics.avgLatencyMs}ms` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground">Latency</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1">
                  <span>By {fn.author}</span>
                  {fn.lastDeployed && <span>Deployed {new Date(fn.lastDeployed).toLocaleDateString()}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Code className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No functions found</p>
          <p className="text-xs text-muted-foreground/70 mt-1 mb-3">Create custom TypeScript or Python functions to extend agent capabilities</p>
          {canCreateFunction && (
            <Button variant="outline" size="sm" onClick={() => navigate({ to: '/connections/new-function', search: { edit: '' } })}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> New Function
            </Button>
          )}
        </div>
      )}

      {/* Code Editor Sheet */}
      <Sheet open={!!editorFn} onOpenChange={open => {
        if (!open) {
          if (editorDirty) {
            if (!confirm('You have unsaved changes. Discard?')) return;
          }
          setSelectedFnId(null); setEditorCode(''); setEditorDirty(false); setTestResult(null); setTestOpen(false); setTestInput(''); setEditorFullscreen(false);
        }
      }}>
        <SheetContent className={cn('p-0 flex flex-col', editorFullscreen ? 'w-[95vw] sm:max-w-[95vw]' : 'w-[900px] sm:max-w-[900px]')}>
          {editorFn && (
            <>
              {/* Editor header */}
              <div className="px-4 py-2.5 border-b border-border/40 shrink-0">
                <div className="flex items-center justify-between">
                  <SheetHeader className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <SheetTitle className="text-sm">{editorFn.displayName}</SheetTitle>
                      <Badge variant="outline" className={cn('text-[9px]', LANGUAGE_LABELS[editorFn.language].color)}>
                        {LANGUAGE_LABELS[editorFn.language].label}
                      </Badge>
                      <Badge variant="outline" className={cn('text-[9px]', FUNCTION_STATUS_STYLES[editorFn.status])}>
                        {editorFn.status}
                      </Badge>
                      {editorDirty && <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400">Unsaved</Badge>}
                    </div>
                    {/* Codex retest #243 — function `name` slug doubles
                        as the accessible description so Radix doesn't
                        warn about missing aria-describedby on the editor
                        sheet. Visual presentation is unchanged. */}
                    <SheetDescription className="text-xs font-mono text-muted-foreground">
                      {editorFn.name}
                    </SheetDescription>
                  </SheetHeader>
                  {/* Toolbar */}
                  <div className="flex items-center gap-1 ml-3">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Format code (Ctrl+Shift+F)"
                      onClick={() => editorRef.current?.getAction('editor.action.formatDocument')?.run()}>
                      <AlignLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Copy code"
                      onClick={() => { navigator.clipboard.writeText(editorCode || editorFn.code); toast.success('Code copied'); }}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={editorFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                      onClick={() => setEditorFullscreen(!editorFullscreen)}>
                      {editorFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Read-only banner for platform-managed system functions */}
              {editorFn.isSystem && (
                <div className="px-4 py-2 bg-slate-500/5 border-b border-slate-500/20 flex items-center gap-2 shrink-0">
                  <Lock className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" />
                  <span className="text-xs text-slate-600 dark:text-slate-400">Platform-managed system function — read-only. The source is maintained by your platform administrator.</span>
                </div>
              )}

              {/* Read-only banner for active functions */}
              {!editorFn.isSystem && editorFn.status === 'active' && (
                <div className="px-4 py-2 bg-amber-500/5 border-b border-amber-500/20 flex items-center gap-2 shrink-0">
                  <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                  <span className="text-xs text-amber-600 dark:text-amber-400">This function is deployed and active — disable to edit.</span>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] ml-auto" onClick={() => { handleDisable(editorFn.id); }}>
                    Disable
                  </Button>
                </div>
              )}

              {/* Monaco editor — shared <FunctionSourceEditor> primitive.
                  Pre-refactor: 14-option inline <Editor> with custom
                  onMount logic (cursor tracking + Ctrl+S + TS diag tune).
                  Variant='tenant-sheet' preserves all 14 options.
                  See FunctionSourceEditor.helpers.ts buildFunctionEditorOptions(). */}
              <div className="flex-1 min-h-0">
                <FunctionSourceEditor
                  variant="tenant-sheet"
                  language={editorFn.language}
                  value={editorCode}
                  readOnly={editorFn.status === 'active' || editorFn.isSystem === true}
                  theme={mode === 'dark' ? 'dark' : 'light'}
                  fullscreen={editorFullscreen}
                  onChange={(v) => { setEditorCode(v); setEditorDirty(v !== editorFn.code); }}
                  onMount={(editor, monaco) => {
                    editorRef.current = editor;
                    setEditorCode(editorFn.code);
                    // Track cursor position
                    editor.onDidChangeCursorPosition((e) => {
                      setCursorPos({ line: e.position.lineNumber, col: e.position.column });
                    });
                    // Ctrl+S to save — delegate to the ref so we always run
                    // the latest closure (current buffer + dirty state).
                    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
                      saveSourceRef.current?.();
                    });
                    // Suppress TS semantic errors for mock context (no ambient types)
                    if (editorFn.language === 'typescript') {
                      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                        noSemanticValidation: true,
                        noSyntaxValidation: false,
                      });
                    }
                  }}
                />
              </div>

              {/* Sandbox test panel — opened by the first Test click. The
                  author supplies sample input (single JSON value, or a JSON
                  array of ≤5 inputs), then runs against the isolated-vm
                  sandbox. networkHosts=[] (no egress) + 3s/input cap. */}
              {testOpen && (
                <div className="border-t border-border/40 p-3 max-h-64 overflow-auto shrink-0 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Sample input (JSON value, or a JSON array of up to 5)
                    </span>
                    <span className="text-[10px] text-muted-foreground">Sandbox · no network · 3s/input</span>
                  </div>
                  {editorDirty && (
                    <div className="flex items-center gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                      <XCircle className="h-3 w-3 shrink-0" />
                      Unsaved edits won&apos;t be tested — the sandbox runs the saved source. Save first to test your changes.
                    </div>
                  )}
                  <textarea
                    className="w-full h-16 text-[11px] font-mono bg-muted/30 rounded p-2 border border-border/40 focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                    placeholder='{"value": 42}  — or  [{"a":1},{"a":2}]  — leave empty to run once with {}'
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    spellCheck={false}
                  />
                  {testResult?.running && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running in sandbox...
                    </div>
                  )}
                  {!testResult?.running && testResult?.error && (
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">Test failed to run</span>
                      </div>
                      <p className="text-[11px] font-mono text-red-600 dark:text-red-400 whitespace-pre-wrap">{testResult.error}</p>
                    </div>
                  )}
                  {!testResult?.running && testResult?.results && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {testResult.allPassed ? (
                          <>
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                              All {testResult.results.length} input{testResult.results.length === 1 ? '' : 's'} passed
                            </span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                            <span className="text-xs font-medium text-red-600 dark:text-red-400">
                              {testResult.results.filter((r) => r.error).length} of {testResult.results.length} failed
                            </span>
                          </>
                        )}
                      </div>
                      {testResult.results.map((r, i) => (
                        <div key={i} className="rounded border border-border/40 p-2 space-y-1">
                          <div className="flex items-center gap-2">
                            {r.error ? (
                              <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 shrink-0" />
                            ) : (
                              <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            )}
                            <span className="text-[10px] font-medium text-muted-foreground">Input #{i + 1}</span>
                            <Badge variant="outline" className="text-[9px]">{r.latencyMs}ms</Badge>
                            {r.error && (
                              <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20">
                                {r.error.code}
                              </Badge>
                            )}
                          </div>
                          {r.error ? (
                            <p className="text-[11px] font-mono text-red-600 dark:text-red-400 whitespace-pre-wrap">{r.error.message}</p>
                          ) : (
                            <pre className="text-[11px] font-mono text-muted-foreground bg-muted/30 rounded p-2 overflow-auto">
                              {JSON.stringify(r.output, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Editor footer */}
              <div className="px-4 py-2 border-t border-border/40 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-mono text-muted-foreground">Ln {cursorPos.line}, Col {cursorPos.col}</span>
                  <Badge variant="secondary" className="text-[9px]">
                    {editorFn.docker.cpuLimit} CPU · {editorFn.docker.memoryLimit}
                  </Badge>
                  <Badge variant="secondary" className="text-[9px]">
                    {editorFn.docker.sandboxed ? 'Sandboxed' : 'Unrestricted'}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">Ctrl+S to save</span>
                </div>
                <div className="flex items-center gap-2">
                  {editorFn.isSystem ? (
                    /* System functions are platform-managed — no tenant-side
                       edit/test/deploy. Source is redacted server-side. */
                    <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300 border-slate-300 dark:border-slate-700">
                      <Lock className="h-2.5 w-2.5 mr-0.5" /> System · read-only
                    </Badge>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs" onClick={() => {
                        setSelectedFnId(null);
                        navigate({ to: '/connections/new-function', search: { edit: editorFn.id } });
                      }}>
                        <Sparkles className="h-3 w-3" /> Edit with AI
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 h-7 text-xs"
                        onClick={handleRunTest}
                        disabled={testResult?.running || testFn.isPending}
                        title={editorFn.language === 'python'
                          ? 'Sandbox test supports TypeScript functions only'
                          : 'Run this function in the isolated-vm sandbox'}
                      >
                        {testResult?.running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                        {testOpen ? 'Run' : 'Test'}
                      </Button>
                      {editorDirty && editorFn.status !== 'active' && (
                        <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs" disabled={saveSource.isPending} onClick={() => { handleSaveSource(editorFn.id, editorCode); }}>
                          {saveSource.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                          Save
                        </Button>
                      )}
                      {editorFn.status !== 'active' && (
                        <Button size="sm" className="gap-1.5 h-7 text-xs" onClick={() => { handleDeploy(editorFn.id); }}>
                          <Rocket className="h-3 w-3" /> Deploy
                        </Button>
                      )}
                      {editorFn.status === 'active' && (
                        <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> Live
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Function</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.displayName}</strong>?
              This will remove the function and all its deployed containers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete Function</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
